'use client';

import type { ReactNode } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';

export type FaqEntry = {
  question: ReactNode;
  answer: ReactNode;
};

type Props = {
  /** Small uppercase kicker above the title, e.g. "FAQ". */
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  items: readonly FaqEntry[];
  /** Anchor target. Nav links and in-page jumps rely on `#faq`. */
  id?: string;
  /**
   * `light` is the site-wide default. `dark` exists only for pages whose whole
   * `<main>` is dark (/guarantee) — same layout, inverted palette, so the
   * section doesn't punch a white hole through the page.
   */
  tone?: 'light' | 'dark';
  /** Optional CTA rendered under the accordion. */
  footer?: ReactNode;
  className?: string;
};

export const FaqSection = ({
  eyebrow,
  title,
  subtitle,
  items,
  id = 'faq',
  tone = 'light',
  footer,
  className,
}: Props) => {
  const dark = tone === 'dark';

  return (
    <section
      id={id}
      className={cn(
        'scroll-mt-24 py-32',
        dark ? 'bg-black text-white' : 'bg-white',
        className,
      )}
    >
      <div className="container space-y-16">
        <div className="mx-auto flex max-w-3xl flex-col justify-center gap-7 text-center">
          {eyebrow ? (
            <p
              className={cn(
                'text-xs font-medium uppercase ltr:tracking-widest',
                dark ? 'text-neutral-400' : 'text-neutral-500',
              )}
            >
              {eyebrow}
            </p>
          ) : null}
          <h2
            className={cn(
              'font-serif text-2xl md:text-4xl',
              dark && 'text-white',
            )}
          >
            {title}
          </h2>
          {subtitle ? (
            <p
              className={cn(
                'text-sm md:text-base',
                dark ? 'text-neutral-400' : 'text-muted-foreground',
              )}
            >
              {subtitle}
            </p>
          ) : null}
        </div>

        <Accordion
          type="single"
          collapsible
          className="mx-auto w-full lg:max-w-3xl"
        >
          {items.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className={cn(dark && 'border-white/10')}
            >
              <AccordionTrigger className="transition-opacity duration-200 hover:no-underline hover:opacity-60">
                <div className="font-medium sm:py-1 lg:py-2 lg:text-lg">
                  {item.question}
                </div>
              </AccordionTrigger>
              <AccordionContent className="sm:mb-1 lg:mb-2">
                <div
                  className={cn(
                    'lg:text-lg',
                    dark ? 'text-neutral-400' : 'text-muted-foreground',
                  )}
                >
                  {item.answer}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {footer ? <div className="flex justify-center">{footer}</div> : null}
      </div>
    </section>
  );
};

export default FaqSection;
